import React, { useCallback, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Divider, makeStyles, IconButton, Tooltip } from '@material-ui/core'
import FlexRow from '../../../../components/molecules/FlexRow'
import FlexColumn from '../../../../components/molecules/FlexColumn'
import Icon from '../../../../components/atoms/Icon'
import { useProvideFeedback } from '../../../../api/denaliAssistant'
import MessageAttachments from './MessageAttachments'
import MessageFeedbackModal from './MessageFeedbackModal'

const useStyles = makeStyles(() => ({
  footer: {
    marginTop: 'var(--margin-sm)'
  },
  actionsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingTop: 'var(--padding-xs)',
    paddingBottom: 'var(--padding-xs)'
  },
  attachmentsContainer: {
    flex: 1,
    '& .mt-3': {
      marginTop: 0
    }
  },
  buttonsContainer: {
    display: 'flex',
    gap: '2px',
    alignItems: 'center'
  },
  iconButton: {
    padding: '4px',
    '&:hover': {
      backgroundColor: 'var(--dai-color-surface2)'
    }
  }
}))

function MessageFooter ({ message, onRetry, isRetrying, parentInfo }) {
  const classes = useStyles()
  const [copyTooltipText, setCopyTooltipText] = useState('Copy message')
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const [pendingFeedbackType, setPendingFeedbackType] = useState(null)
  const feedbackMutation = useProvideFeedback()

  const selectedFeedback = useMemo(() => {
    return message.feedback?.type || null
  }, [message.feedback?.type])

  const feedbackType = useMemo(() => {
    return pendingFeedbackType || 'thumbs_up'
  }, [pendingFeedbackType])

  const handleCopy = useCallback(() => {
    if (message.message) {
      navigator.clipboard.writeText(message.message)
        .then(() => {
          setCopyTooltipText('Copied!')
          setTimeout(() => {
            setCopyTooltipText('Copy message')
          }, 2000)
        })
        .catch((error) => {
          console.error('Failed to copy message:', error)
          setCopyTooltipText('Failed to copy')
          setTimeout(() => {
            setCopyTooltipText('Copy message')
          }, 2000)
        })
    }
  }, [message.message])

  const handleThumbsUp = useCallback(() => {
    if (message.threadMessageId) {
      setPendingFeedbackType('thumbs_up')
      setFeedbackModalOpen(true)
    }
  }, [message.threadMessageId])

  const handleThumbsDown = useCallback(() => {
    if (message.threadMessageId) {
      setPendingFeedbackType('thumbs_down')
      setFeedbackModalOpen(true)
    }
  }, [message.threadMessageId])

  const handleFeedbackSubmit = useCallback((feedbackText) => {
    if (message.threadMessageId && pendingFeedbackType && parentInfo?.parentId) {
      feedbackMutation.mutate({
        threadMessageId: message.threadMessageId,
        threadId: parentInfo.parentId,
        type: pendingFeedbackType,
        feedbackText: feedbackText || undefined
      })
    }
    setFeedbackModalOpen(false)
    setPendingFeedbackType(null)
  }, [message.threadMessageId, pendingFeedbackType, feedbackMutation, parentInfo?.parentId])

  const handleFeedbackCancel = useCallback(() => {
    setFeedbackModalOpen(false)
    setPendingFeedbackType(null)
  }, [])

  const handleRetry = useCallback(() => {
    if (onRetry) {
      onRetry(message)
    }
  }, [onRetry, message])

  return (
    <FlexColumn className={classes.footer}>
      <Divider />
      <FlexRow className={classes.actionsRow}>
        <div className={classes.attachmentsContainer}>
          <MessageAttachments message={message} />
        </div>
        <div className={classes.buttonsContainer}>
          <Tooltip title={copyTooltipText} arrow>
            <IconButton
              size='small'
              onClick={handleCopy}
              className={classes.iconButton}
            >
              <Icon name='copy' style={{ fontSize: '16px' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title='Thumbs up' arrow>
            <IconButton
              size='small'
              onClick={handleThumbsUp}
              className={classes.iconButton}
            >
              <Icon
                name='thumbUp'
                style={{
                  fontSize: '16px',
                  color: selectedFeedback === 'thumbs_up' ? 'var(--color-primary-500)' : undefined
                }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title='Thumbs down' arrow>
            <IconButton
              size='small'
              onClick={handleThumbsDown}
              className={classes.iconButton}
            >
              <Icon
                name='thumbDown'
                style={{
                  fontSize: '16px',
                  color: selectedFeedback === 'thumbs_down' ? 'var(--color-primary-500)' : undefined
                }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title='Retry' arrow>
            <IconButton
              size='small'
              onClick={handleRetry}
              disabled={isRetrying}
              className={classes.iconButton}
            >
              <Icon name='sync' style={{ fontSize: '16px' }} />
            </IconButton>
          </Tooltip>
        </div>
      </FlexRow>
      <MessageFeedbackModal
        open={feedbackModalOpen}
        onClose={handleFeedbackCancel}
        feedbackType={feedbackType}
        onSubmit={handleFeedbackSubmit}
      />
    </FlexColumn>
  )
}

MessageFooter.propTypes = {
  message: PropTypes.shape({
    threadMessageId: PropTypes.string,
    isPlaceholder: PropTypes.bool,
    actor: PropTypes.string,
    isGenerating: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    generationStatus: PropTypes.string,
    createdDate: PropTypes.string,
    message: PropTypes.string,
    data: PropTypes.object,
    feedback: PropTypes.shape({
      type: PropTypes.oneOf(['thumbs_up', 'thumbs_down'])
    })
  }).isRequired,
  onRetry: PropTypes.func,
  isRetrying: PropTypes.bool,
  parentInfo: PropTypes.shape({
    parentId: PropTypes.string,
    parentType: PropTypes.oneOf(['schedule', 'thread'])
  })
}

export default MessageFooter
